import { MODERATION_TEXTS, type Generation } from '@trendator/shared';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { api } from '../api';
import { BackIcon, DownloadIcon, RepeatIcon, ShareIcon, TickIcon } from '../components/icons';
import { plural, rub } from '../format';
import { useBackButton } from '../hooks';
import { canShareToStory, closeApp, downloadFile, haptic, hapticResult, shareToStory } from '../telegram';
import { SongDone, StickersDone, VideoDone, qcText } from './MediaResult';
import { Loading } from './Status';
import { ReportLink } from '../components/ReportSheet';
import { track } from '../analytics';

const POLL_MS = 2000;

/** Прогресс → Готово / Возврат (docs/design/Progress, Result, Refund). */
export function GenerationStatus({
  generationId,
  onBalance,
  onHome,
  onRepeat,
}: {
  generationId: string;
  onBalance: (balance: number) => void;
  onHome: () => void;
  /** «Ещё раз»: ручной режим — с теми же параметрами, тренд — снова экран тренда. */
  onRepeat: (gen: Generation) => void;
}) {
  useBackButton(onHome);
  const [gen, setGen] = useState<Generation | null>(null);
  const [missing, setMissing] = useState(false);
  const loaded = useRef(false);
  const notified = useRef(false);
  const onBalanceRef = useRef(onBalance);
  onBalanceRef.current = onBalance;

  const load = useCallback(async () => {
    try {
      const g = await api.generation(generationId);
      loaded.current = true;
      setGen(g);
      onBalanceRef.current(g.balance);
      return g;
    } catch {
      if (!loaded.current) setMissing(true);
      return null;
    }
  }, [generationId]);

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      const g = await load();
      if (stopped || (!g && !loaded.current)) return;
      if (!g || g.status === 'queued' || g.status === 'running') timer = setTimeout(tick, POLL_MS);
    };
    void tick();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [load]);

  useEffect(() => {
    if (!gen || notified.current || (gen.status !== 'succeeded' && gen.status !== 'failed')) return;
    notified.current = true;
    hapticResult(gen.status === 'succeeded' ? 'success' : 'error');
  }, [gen]);

  if (missing) {
    return (
      <main className="screen screen--hero">
        <div className="center" style={{ minHeight: 'calc(80 * var(--dvh))' }}>
          <h1>Не нашли генерацию</h1>
          <p>Возможно, она старше 30 дней — мы храним результаты месяц.</p>
          <button type="button" className="btn-accent" style={{ maxWidth: 280 }} onClick={onHome}>
            На главную
          </button>
        </div>
      </main>
    );
  }
  if (!gen) return <Loading />;

  const repeat = () => onRepeat(gen);

  if (gen.status === 'succeeded') {
    const footer = <ReportLink generationId={gen.id} />;
    if (gen.kind === 'stickers') return <StickersDone gen={gen} onBack={onHome} onRepeat={repeat} footer={footer} />;
    const type = gen.results[0]?.type;
    if (type === 'video') return <VideoDone gen={gen} took={duration(gen)} onBack={onHome} onRepeat={repeat} footer={footer} />;
    if (type === 'audio') return <SongDone gen={gen} onBack={onHome} onRepeat={repeat} footer={footer} />;
    return <Done gen={gen} onBack={onHome} onRepeat={repeat} footer={footer} />;
  }
  if (gen.status === 'failed') return <Refund gen={gen} onHome={onHome} onRepeat={repeat} />;
  return <Progress gen={gen} onMinimize={() => closeApp() || onHome()} />;
}

/** Оценка прогресса по времени: честно не больше 95%, пока не готово. */
function useProgress(gen: Generation): { pct: number; leftSec: number } {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, []);
  const elapsed = Math.max(0, (now - new Date(gen.createdAt).getTime()) / 1000);
  const pct = Math.min(95, Math.round((elapsed / gen.etaSec) * 100));
  return { pct, leftSec: Math.max(1, Math.round(gen.etaSec - elapsed)) };
}

function progressTitle(gen: Generation): string {
  if (gen.kind === 'video') return gen.frames > 1 ? `Снимаем ${gen.frames} видео` : 'Снимаем видео';
  if (gen.kind === 'song') return 'Пишем песню';
  if (gen.kind === 'stickers') return 'Рисуем стикеры';
  if (gen.source === 'trend' && gen.frames > 1) return `Рисуем ${gen.frames} ${plural(gen.frames, 'версию', 'версии', 'версий')} тебя`;
  return gen.frames > 1 ? `Рисуем ${gen.frames} фото` : 'Рисуем фото';
}

function Progress({ gen, onMinimize }: { gen: Generation; onMinimize: () => void }) {
  const { pct, leftSec } = useProgress(gen);
  const steps = [
    { label: 'В очереди', active: gen.status === 'queued', done: gen.status !== 'queued' },
    { label: 'Генерируем', active: gen.status === 'running', done: false },
    ...(gen.qc ? [{ label: 'Проверяем качество', active: false, done: false }] : []),
    { label: 'Сохраняем результат', active: false, done: false },
  ];
  return (
    <main className="screen progress">
      <header className="progress__header">
        <span className="progress__title">{gen.title}</span>
      </header>

      <div className="progress__card-wrap" aria-hidden="true">
        <div className="progress__glow" />
        <div className="progress__card">
          <div className="ring" style={{ background: `conic-gradient(var(--accent) 0 ${pct}%, rgba(255,255,255,0.14) ${pct}% 100%)` }}>
            <div className="ring__inner">
              <span className="ring__pct">{pct}%</span>
              <span className="ring__eta">{pct >= 95 ? 'почти' : leftSec >= 90 ? `≈ ${Math.round(leftSec / 60)} мин` : `≈ ${leftSec} сек`}</span>
            </div>
          </div>
        </div>
      </div>

      <h1 className="progress__h1" role="status">
        {progressTitle(gen)}
      </h1>

      <div className="glass-list progress__steps">
        {steps.map((s) => (
          <div key={s.label} className={`step${s.active ? ' step--active' : ''}${s.done ? ' step--done' : ''}`}>
            <span className="step__dot" />
            <span className="step__label">{s.label}</span>
            {s.active && gen.frames > 1 && (
              <span className="step__count">
                {gen.framesDone} из {gen.frames}
              </span>
            )}
          </div>
        ))}
      </div>

      <div className="progress__note">Можно закрыть приложение — пришлём результат в чат с ботом</div>

      <div className="result__footer">
        <button type="button" className="btn-glass" onClick={onMinimize}>
          Свернуть и ждать в боте
        </button>
      </div>
    </main>
  );
}

function duration(gen: Generation): string {
  if (!gen.finishedAt) return '';
  const sec = Math.max(1, Math.round((new Date(gen.finishedAt).getTime() - new Date(gen.createdAt).getTime()) / 1000));
  return sec >= 60 ? `${Math.floor(sec / 60)} мин ${sec % 60} с` : `${sec} с`;
}

function Done({ gen, onBack, onRepeat, footer }: { gen: Generation; onBack: () => void; onRepeat: () => void; footer?: ReactNode }) {
  const [sel, setSel] = useState(0);
  const ratio = (gen.aspectRatio ?? '9:16').replace(':', ' / ');
  const many = gen.results.length > 1;
  const current = gen.results[sel] ?? gen.results[0];
  return (
    <main className="screen done">
      <header className="topbar">
        <button type="button" className="glass icon-btn" aria-label="Назад" onClick={onBack}>
          <BackIcon />
        </button>
        {many ? <span className="done__count">{gen.results.length} фото</span> : null}
      </header>
      <div className="topup__intro">
        <h1 className="page-title">Готово</h1>
        <p className="page-lead">
          {duration(gen)}
          {gen.free ? ' · бесплатно' : ` · ${rub(gen.price)}`}
          {gen.frames > gen.results.length ? ` · ${gen.frames - gen.results.length} не вышло` : ''}
        </p>
        {qcText(gen) ? (
          <p className="done__qc">
            <TickIcon />
            {qcText(gen)}
          </p>
        ) : null}
      </div>

      {many ? (
        <div className="done__grid">
          {gen.results.map((r, i) => (
            <button
              key={r.url}
              type="button"
              className={`done__cell${i === sel ? ' done__cell--on' : ''}`}
              aria-label={r.label || `Фото ${i + 1}`}
              aria-pressed={i === sel}
              onClick={() => {
                haptic('select');
                setSel(i);
              }}
            >
              <img src={r.url} alt="" />
              {r.label ? <span className="done__label">{r.label}</span> : null}
            </button>
          ))}
        </div>
      ) : null}

      {current && !many && (
        <div className="done__media">
          <img src={current.url} alt={`${gen.title}${current.label ? ` — ${current.label}` : ''}`} style={{ aspectRatio: ratio }} />
        </div>
      )}

      {current && (
        <div className="actions3">
          <button
            type="button"
            onClick={() => {
              haptic();
              downloadFile(current.url, `trendator-${gen.id.slice(0, 8)}${many ? `-${sel + 1}` : ''}.jpg`);
            }}
          >
            <DownloadIcon />
            Скачать
          </button>
          {canShareToStory() && (
            <button
              type="button"
              onClick={() => {
                haptic();
                shareToStory(current.url);
                track('share', { what: 'story' });
              }}
            >
              <ShareIcon />В сторис
            </button>
          )}
          <button type="button" onClick={onRepeat}>
            <RepeatIcon />
            Ещё раз
          </button>
        </div>
      )}
      {many ? <p className="done__hint">Все фото уже в чате с ботом — альбомом, в полном качестве.</p> : null}
      {footer}
    </main>
  );
}

function Refund({ gen, onHome, onRepeat }: { gen: Generation; onHome: () => void; onRepeat: () => void }) {
  const amount = gen.free ? 0 : gen.price;
  const moderated = gen.error === MODERATION_TEXTS.output_blocked;
  return (
    <main className="screen result result--refund">
      <header className="topbar">
        <button type="button" className="glass icon-btn" aria-label="Назад" onClick={onHome}>
          <BackIcon />
        </button>
      </header>
      <div className="result__head" style={{ paddingTop: 40 }}>
        <span className="refund__meta">
          {gen.title} · {moderated ? 'не прошло модерацию' : 'модель не ответила'}
        </span>
        <h1 className="result__title result__title--md" style={{ marginTop: 8 }}>
          {gen.free ? (
            <>
              Бесплатная попытка
              <br />
              вернулась
            </>
          ) : (
            <>
              {rub(amount)} уже
              <br />
              вернули на баланс
            </>
          )}
        </h1>
        <p className="result__text">
          {moderated
            ? 'Результат вышел за правила (18+, насилие, знаменитости) — показать его не можем. Попробуй переформулировать.'
            : 'Ты ничего не потерял. Мы не берём деньги за чужие ошибки.'}
        </p>
      </div>

      {!gen.free && (
        <div className="glass-list refund__balance">
          <span className="refund__label">Баланс</span>
          <span className="refund__value">
            {rub(gen.balance)} <span className="refund__plus">+{amount}</span>
          </span>
        </div>
      )}

      <div className="result__footer">
        <button type="button" className="btn-accent" onClick={onRepeat}>
          {moderated ? 'Изменить запрос' : `Запустить ещё раз${gen.free ? '' : ` · ${rub(gen.price)}`}`}
        </button>
      </div>
    </main>
  );
}
